import React, { useEffect, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useNotification } from '../../hooks/NotificationContext';
import AutomationDetailsForm from './AutomationDetailsForm';
import Loader from '../elements/Loader';
import CustomSelect from '../../ui/primitives/CustomSelect';
import { addGithubActionsDetails, addJenkinsDetails } from '../../modules/automation';
import { getCurrentProject } from '../../modules/project';

const executionToolOptions = [
  { id: 'jenkins', name: 'Jenkins' },
  { id: 'github', name: 'GitHub Actions' },
];

const AutomationIntegration = ({ isAdmin }) => {
  const [error, setError] = useState(null);
  const [selectedAutomation, setSelectedAutomation] = useState('');
  const { addNotification } = useNotification();
  const [showAuthToken, setShowAuthToken] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { projectId } = useParams();

  const defaultJenkinsData = {
    jenkinsurl: '',
    jenkinsUsername: '',
    jenkinsAuthToken: '',
  };

  const defaultGithubData = {
    githubRepo: '',
    githubUsername: '',
    githubAuthToken: '',
  };

  const [jenkinsData, setJenkinsData] = useState(defaultJenkinsData);
  const [githubData, setGithubData] = useState(defaultGithubData);

  const fetchProjectData = async () => {
    setLoading(true);
    try {
      const response = await getCurrentProject(projectId);
      setSelectedAutomation(response?.automation_method || 'jenkins');
      setJenkinsData({
        jenkinsurl: response.jenkinsurl || '',
        jenkinsUsername: response.jenkins_username || '',
        jenkinsAuthToken: response.jenkins_auth_token || '',
      });
      setGithubData({
        githubRepo: response.github_repo || '',
        githubUsername: response.github_username || '',
        githubAuthToken: response.github_auth_token || '',
      });
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    if (selectedAutomation === 'jenkins') {
      setJenkinsData((prevData) => ({ ...prevData, [id]: value }));
    } else {
      setGithubData((prevData) => ({ ...prevData, [id]: value }));
    }
  };

  const handleSaveClick = async () => {
    const data = selectedAutomation === 'jenkins' ? jenkinsData : githubData;
    const isJenkins = selectedAutomation === 'jenkins';
    const isValid = isJenkins
      ? data.jenkinsurl && data.jenkinsUsername && data.jenkinsAuthToken
      : data.githubRepo && data.githubUsername && data.githubAuthToken;

    if (isValid) {
      setSubmitting(true);
      try {
        isJenkins
          ? await addJenkinsDetails({ ...data, automationMethod: selectedAutomation }, projectId)
          : await addGithubActionsDetails({ ...data, automationMethod: selectedAutomation }, projectId);

        addNotification(
          'success',
          `${isJenkins ? 'Jenkins' : 'GitHub Actions'} details saved successfully`
        );
        fetchProjectData();
      } catch (error) {
        addNotification('danger', error.message || `Failed to save ${selectedAutomation} details`);
      }
      setSubmitting(false);
    } else {
      setError('Please fill all the required fields');
    }
  };

  useEffect(() => {
    fetchProjectData();
  }, []);

  if (loading) return <Loader />;

  return (
    <Box>
      <Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <CustomSelect
              label="Select Execution Tool"
              required
              value={selectedAutomation}
              onChange={setSelectedAutomation}
              options={executionToolOptions}
              disabled={!isAdmin}
              placeholder="Select tool"
            />
          </Grid>
        </Grid>

        <AutomationDetailsForm
          data={selectedAutomation === 'jenkins' ? jenkinsData : githubData}
          handleChange={handleChange}
          handleSave={handleSaveClick}
          showAuthToken={showAuthToken}
          setShowAuthToken={setShowAuthToken}
          submitting={submitting}
          error={error}
          readOnly={!isAdmin}
          labels={
            selectedAutomation === 'jenkins'
              ? {
                  urlLabel: 'Jenkins URL',
                  usernameLabel: 'Jenkins Username',
                  tokenLabel: 'Jenkins Auth Token',
                  urlPlaceholder: 'jenkinsurl',
                  usernamePlaceholder: 'jenkinsUsername',
                  tokenPlaceholder: 'jenkinsAuthToken',
                }
              : {
                  urlLabel: 'GitHub Repo Name',
                  usernameLabel: 'GitHub Username',
                  tokenLabel: 'GitHub Auth Token',
                  urlPlaceholder: 'githubRepo',
                  usernamePlaceholder: 'githubUsername',
                  tokenPlaceholder: 'githubAuthToken',
                }
          }
        />
      </Box>
    </Box>
  );
};

export default AutomationIntegration;
